import { forwardRef, InputHTMLAttributes, ReactNode, useId } from 'react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  helperText?: string;
  hint?: string;
  error?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  containerClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      hint,
      error,
      leftIcon,
      rightIcon,
      id,
      disabled,
      className = '',
      containerClassName = '',
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const hasError = Boolean(error);

    return (
      <div className={`w-full space-y-1.5 ${containerClassName}`}>
        {label && (
          <label
            htmlFor={inputId}
            className="block cursor-pointer text-xs font-semibold text-content-main"
          >
            {label}
          </label>
        )}

        <div className="relative flex items-center">
          {leftIcon && (
            <div className="pointer-events-none absolute left-3 flex items-center text-content-muted">
              {leftIcon}
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            aria-invalid={hasError}
            aria-describedby={
              hasError
                ? `${inputId}-error`
                : helperText
                  ? `${inputId}-helper`
                  : undefined
            }
            className={`w-full rounded-xl border bg-surface-subtle py-2.5 text-xs text-content-main outline-none transition-all duration-150 placeholder:text-content-muted sm:text-sm ${leftIcon ? 'pl-9' : 'pl-3.5'} ${rightIcon ? 'pr-9' : 'pr-3.5'} ${
              hasError
                ? 'border-red-500/80 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                : 'border-surface-border focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20'
            } ${
              disabled
                ? 'bg-surface-canvas/50 cursor-not-allowed opacity-50'
                : 'hover:border-surface-border/80'
            } ${className} `}
            {...props}
          />

          {rightIcon && (
            <div className="absolute right-3 flex items-center text-content-muted">
              {rightIcon}
            </div>
          )}
        </div>

        {hasError ? (
          <p
            id={`${inputId}-error`}
            role="alert"
            className="pl-0.5 text-micro text-red-600 dark:text-red-400"
          >
            {error}
          </p>
        ) : helperText || hint ? (
          <p
            id={`${inputId}-helper`}
            className="pl-0.5 text-micro text-content-muted"
          >
            {helperText || hint}
          </p>
        ) : null}
      </div>
    );
  }
);

Input.displayName = 'Input';
